import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getRootFolders,
  getPersonalSubrootFolders,
  getFoldersByGroupAndType,
} from '../../api/edms/folders';

const GROUP_CATEGORIES = ['Confidential', 'General', 'Personal'];

function toFolderOptions(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((f) => {
      const id = f?._id ?? f?.id ?? f?.folderId;
      const label = f?.folderName ?? f?.name ?? f?.label ?? f?.folderNumber ?? 'Folder';
      const folderTypeId =
        f?.folderTypeId ??
        f?.folderType?._id ??
        f?.folderType?.id ??
        f?.folderType?.folderTypeId ??
        '';
      if (!id) return null;
      return { value: String(id), label: String(label), folderTypeId: String(folderTypeId || '') };
    })
    .filter(Boolean);
}

/**
 * Filing folder picker options (NEW_ARCHIVE).
 * - roots: /api/folders/roots (Confidential, General, Personal)
 * - personal subroots: /api/folders/roots/personal-subroots (Staff, Members)
 * - folders list: /api/folders/by-group-and-type?groupCategory=...&groupSubCategory=...&folderTypeId=...
 */
export function useFilingFolderOptions({
  groupCategory,
  groupSubCategory,
  folderTypeId,
  enabled = true,
} = {}) {
  const rootsQuery = useQuery({
    queryKey: ['edms', 'folders', 'roots'],
    queryFn: () => getRootFolders(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const personalSubrootsQuery = useQuery({
    queryKey: ['edms', 'folders', 'roots', 'personal-subroots'],
    queryFn: () => getPersonalSubrootFolders(),
    enabled: enabled && String(groupCategory || '') === 'Personal',
    staleTime: 5 * 60 * 1000,
  });

  const foldersQuery = useQuery({
    queryKey: [
      'edms',
      'folders',
      'by-group-and-type',
      { groupCategory, groupSubCategory, folderTypeId },
    ],
    queryFn: () =>
      getFoldersByGroupAndType({
        groupCategory,
        groupSubCategory: groupCategory === 'Personal' ? groupSubCategory || undefined : undefined,
        folderTypeId: folderTypeId || undefined,
      }),
    enabled:
      enabled &&
      Boolean(groupCategory) &&
      (String(groupCategory) !== 'Personal' || Boolean(groupSubCategory)),
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
  });

  const groupCategoryOptions = useMemo(() => {
    // Prefer server-provided roots if present; otherwise fall back to known categories.
    const raw = rootsQuery.data?.data ?? rootsQuery.data;
    const roots = Array.isArray(raw) ? raw : raw?.roots ?? raw?.items ?? null;
    const names = Array.isArray(roots)
      ? roots
          .map((r) => r?.folderName ?? r?.name)
          .filter(Boolean)
      : [];
    const unique = [...new Set((names.length > 0 ? names : GROUP_CATEGORIES).map(String))];
    return unique.map((name) => ({ value: name, label: name }));
  }, [rootsQuery.data]);

  const groupSubCategoryOptions = useMemo(() => {
    const raw = personalSubrootsQuery.data?.data ?? personalSubrootsQuery.data;
    const items = Array.isArray(raw) ? raw : raw?.folders ?? raw?.items ?? raw?.data ?? [];
    const serverOptions = toFolderOptions(items);
    if (serverOptions.length > 0) return serverOptions;
    return [
      { value: 'Staff', label: 'Staff' },
      { value: 'Members', label: 'Members' },
    ];
  }, [personalSubrootsQuery.data]);

  const folderOptions = useMemo(() => {
    const raw = foldersQuery.data?.data ?? foldersQuery.data;
    const items = raw?.folders ?? raw?.items ?? raw?.data ?? raw;
    return toFolderOptions(items);
  }, [foldersQuery.data]);

  return {
    groupCategoryOptions,
    groupSubCategoryOptions,
    folderOptions,
    rootsQuery,
    personalSubrootsQuery,
    foldersQuery,
    isLoading: rootsQuery.isLoading || personalSubrootsQuery.isLoading || foldersQuery.isLoading,
    isError: rootsQuery.isError || personalSubrootsQuery.isError || foldersQuery.isError,
    error: rootsQuery.error || personalSubrootsQuery.error || foldersQuery.error,
  };
}

export default useFilingFolderOptions;

